import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";

import { RootState } from "../../../store";
import { validateEmail } from "../../../utils";
import { warningActions } from "../../../store/slices/WarningSlice";
import { getMemberDetailsFromPortal } from "../../../services/orders";
import { BASE_URL, CONTACT_DETAILS_URL, POD_URL } from "../../../Routes/urls";
import Modal, { ModalBody, ModalFooter } from "../../../components/Modal/Modal";
import Input from "../../../components/Inputs/Input";
import classes from "./PatientLogin.module.css";
import PrimaryButton from "../../../components/Buttons/PrimaryButton";
import LinkButton from "../../../components/Buttons/LinkButton";
import RadioInput from "../../../components/Inputs/RadioInput"

type Props = {
  onClose: () => void;
  eventId?: string;
  openSelectMember?: (data: any[]) => void;
};

const options = {
  fetchData: "FETCH_DATA",
  skip: "SKIP",
};

const getInitialFieldErrors = () => {
  return {
    email: false,
    password: false,
  };
};

const PatientLogin: React.FC<Props> = ({
  onClose,
  eventId,
  openSelectMember,
}) => {
  const { podId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const resourceName = useSelector(
    (state: RootState) => state.pod.resourceName
  );

  const [patientDetails, setPatientDetails] = useState({
    email: "",
    password: "",
  });
  const [selected, setSelected] = useState(options.fetchData);
  const [fieldErrors, setFieldErrors] = useState(getInitialFieldErrors());

  const isFieldsValid = (): boolean => {
    const fieldErrors = getInitialFieldErrors();

    if (!patientDetails.email || !validateEmail(patientDetails.email)) {
      fieldErrors.email = true;
    }

    if (!patientDetails.password) {
      fieldErrors.password = true;
    }

    setFieldErrors(fieldErrors);

    return Object.values(fieldErrors).every((value) => value === false);
  };

  const clearFieldErrors = (key: keyof typeof fieldErrors) => {
    if (fieldErrors[key])
      setFieldErrors((prevState) => {
        const newState = { ...prevState };
        newState[key] = false;
        return newState;
      });
  };

  const handleChange = () => {
    setSelected(options.skip);
    setPatientDetails({ email: "", password: "" });
    clearFieldErrors("email");
    clearFieldErrors("password");
  };

  const proceedHandler = async () => {
    if (options.fetchData === selected) {
      if (!isFieldsValid()) {
        return;
      }

      try {
        const res = await getMemberDetailsFromPortal(patientDetails);
        if (res.status === 200) {
          if (res.data.errorCode === "bad_credentials") {
            dispatch(
              warningActions.showWarning({
                message:
                  "Sorry, we could not retrieve your details from the patient portal. Please check your credentials.",
              })
            );
          } else {
            if (res.data?.patientDetails?.length > 1) {
              openSelectMember?.(res.data?.patientDetails);
            } else {
              localStorage.setItem(
                "patientData",
                JSON.stringify(res.data?.patientDetails[0])
              );
              localStorage.setItem("isPatientPortalData", "true");
              navigate(
                `${BASE_URL}/${resourceName}/${POD_URL}/${podId}/event/${eventId}/${CONTACT_DETAILS_URL}`
              );
            }
          }
        }
      } catch (err) {
        console.log(err);
      }
    } else {
      navigate(
        `${BASE_URL}/${resourceName}/${POD_URL}/${podId}/event/${eventId}/${CONTACT_DETAILS_URL}`
      );
    }
  };

  const forgotPasswordHandler = () => {
    window.open(
      "https://my.reya.net/forgot-password",
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <Modal
      className={classes.modalContainer}
      title={<div className={classes.heading}>Fill Patient Details</div>}
      onClose={onClose}>
      <>
        <ModalBody>
          <div className={classes.main}>
            <div className={classes.description}>
              If you have previously visited the Reya portal and have an
              account, you may retrieve your information by logging in below:
            </div>
            <div>
              <RadioInput
                id='fetch-details'
                name='Sign in to Patient Portal'
                checked={options.fetchData === selected}
                onChange={() => setSelected(options.fetchData)}
              />
              <div className={classes.inputContainer}>
                <Input
                  name='Username'
                  value={patientDetails.email}
                  onChange={(e) => {
                    setPatientDetails((prevState) => {
                      return {
                        ...prevState,
                        email: e.target.value.trim(),
                      };
                    });
                    clearFieldErrors("email");
                  }}
                  isInvalid={fieldErrors.email}
                  invalidText='Invalid Username'
                  disabled={options.fetchData !== selected}
                />
                <Input
                  type='password'
                  name='Password'
                  value={patientDetails.password}
                  onChange={(e) => {
                    setPatientDetails((prevState) => {
                      return {
                        ...prevState,
                        password: e.target.value.trim(),
                      };
                    });
                    clearFieldErrors("password");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      proceedHandler();
                    }
                  }}
                  isInvalid={fieldErrors.password}
                  disabled={options.fetchData !== selected}
                />
              </div>
              <div className={classes.forgotPassword}>
                <LinkButton
                  label={"Forgot Password?"}
                  onClick={forgotPasswordHandler}
                />
              </div>
            </div>
            <div className={classes.skip}>
              <RadioInput
                id='enter-details-manually'
                name='Continue – A link to create your account will be emailed to you after you finish.'
                checked={options.skip === selected}
                onChange={handleChange}
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className={classes.proceedBtnContainer}>
            <PrimaryButton
              name='PROCEED'
              onClick={proceedHandler}
              className={classes.proceedBtn}
            />
          </div>
        </ModalFooter>
      </>
    </Modal>
  );
};

export default PatientLogin;